import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { positionUnwinderAxiosApi } from 'src/Config/axiosApi';
import {
  DebugListResponse,
  DebugPair,
  DebugModeEntry,
  DebugStats,
  UnwindTrace,
  UnwindLog,
  TraceQuery,
  LogQuery,
  TraceLevel,
} from '../types/unwind-debug.types';

function buildParams(q: TraceQuery | LogQuery): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    out[k] = Array.isArray(v) ? v.join(',') : v;
  }
  return out;
}

export function useTraces(query: TraceQuery, opts?: { enabled?: boolean }) {
  return useInfiniteQuery<DebugListResponse<UnwindTrace>>(
    ['unwind-debug', 'traces', query],
    async ({ pageParam }) => {
      const params = buildParams(query);
      if (pageParam) params.cursor = pageParam;
      const { data } = await positionUnwinderAxiosApi.get<DebugListResponse<UnwindTrace>>(
        '/debug/traces',
        { params }
      );
      return data;
    },
    {
      enabled: opts?.enabled !== false,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      staleTime: 1000,
    }
  );
}

export function useLogs(query: LogQuery, opts?: { enabled?: boolean }) {
  return useInfiniteQuery<DebugListResponse<UnwindLog>>(
    ['unwind-debug', 'logs', query],
    async ({ pageParam }) => {
      const params = buildParams(query);
      if (pageParam) params.cursor = pageParam;
      const { data } = await positionUnwinderAxiosApi.get<DebugListResponse<UnwindLog>>(
        '/debug/logs',
        { params }
      );
      return data;
    },
    {
      enabled: opts?.enabled !== false,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      staleTime: 1000,
    }
  );
}

export function useDebugPairs() {
  return useQuery<DebugPair[]>(
    ['unwind-debug', 'pairs'],
    async () => {
      const { data } = await positionUnwinderAxiosApi.get<DebugPair[]>('/debug/pairs');
      return data;
    },
    { refetchInterval: 30_000, staleTime: 10_000 }
  );
}

export function useDebugMode() {
  return useQuery<DebugModeEntry[]>(
    ['unwind-debug', 'debug-mode'],
    async () => {
      const { data } = await positionUnwinderAxiosApi.get<DebugModeEntry[]>('/debug/debug-mode');
      return data;
    },
    { refetchInterval: 15_000 }
  );
}

export function useSetManualDebug() {
  const qc = useQueryClient();
  return useMutation(
    async (params: {
      pairId: string;
      level: TraceLevel | null;
      ttlMinutes?: number;
    }) => {
      const { data } = await positionUnwinderAxiosApi.post<DebugModeEntry | { level: null }>(
        `/debug/debug-mode/${encodeURIComponent(params.pairId)}`,
        { level: params.level, ttlMinutes: params.ttlMinutes }
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries(['unwind-debug', 'debug-mode']) }
  );
}

export function useClearTraces() {
  const qc = useQueryClient();
  return useMutation(
    async (params: { pairId?: string }) => {
      const { data } = await positionUnwinderAxiosApi.delete<{ deletedCount: number }>(
        '/debug/traces',
        {
          params: params.pairId ? { pairId: params.pairId } : {},
          headers: { 'X-Confirm-Clear': 'true' },
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['unwind-debug', 'traces']);
        qc.invalidateQueries(['unwind-debug', 'pairs']);
        qc.invalidateQueries(['unwind-debug', 'stats']);
      },
    }
  );
}

export function useClearLogs() {
  const qc = useQueryClient();
  return useMutation(
    async (params: { pairId?: string }) => {
      const { data } = await positionUnwinderAxiosApi.delete<{ deletedCount: number }>(
        '/debug/logs',
        {
          params: params.pairId ? { pairId: params.pairId } : {},
          headers: { 'X-Confirm-Clear': 'true' },
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['unwind-debug', 'logs']);
        qc.invalidateQueries(['unwind-debug', 'stats']);
      },
    }
  );
}

export function useDebugStats() {
  return useQuery<DebugStats>(
    ['unwind-debug', 'stats'],
    async () => {
      const { data } = await positionUnwinderAxiosApi.get<DebugStats>('/debug/stats');
      return data;
    },
    { refetchInterval: 5_000 }
  );
}

export function useCycleTimeline(cycleId: string | null) {
  return useQuery<{ items: UnwindTrace[] }>(
    ['unwind-debug', 'cycle', cycleId],
    async () => {
      const { data } = await positionUnwinderAxiosApi.get<{ items: UnwindTrace[] }>(
        `/debug/traces/cycles/${encodeURIComponent(cycleId!)}`
      );
      return data;
    },
    { enabled: !!cycleId }
  );
}
